/**
 * <ia-search-suggest>, the field on the search results page, with the same
 * suggestions the drawer shows, dropped under it.
 *
 * It is deliberately thin: the answer comes from the shared predictive module,
 * so the two surfaces cannot start showing different things, and everything
 * else here is about when the panel is open and when it gets out of the way.
 */
import { fetchSuggestions, trimGroups, DEBOUNCE_MS } from 'ia-search-predictive';


class IaSearchSuggest extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.panel = this.querySelector('[data-search-suggest]');
    // No predictive url means the merchant turned suggestions off for the page.
    if (!this.input || !this.panel || !this.dataset.predictiveUrl) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.term = '';

    this.input.addEventListener('input', () => this.queue(), { signal });
    this.input.addEventListener('focus', () => this.queue(), { signal });
    // Escape closes without clearing: the shopper is dismissing the panel, not
    // abandoning what they typed.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.fetchController?.abort();
  }

  queue() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      const term = this.input.value.trim();
      if (term === this.term && this.panel.hidden === false) return;
      this.term = term;
      if (term) this.suggest(term);
      else this.close();
    }, DEBOUNCE_MS);
  }

  async suggest(term) {
    this.fetchController?.abort();
    this.fetchController = new AbortController();
    const { signal } = this.fetchController;
    try {
      const markup = await fetchSuggestions({
        url: this.dataset.predictiveUrl,
        term,
        limit: this.dataset.resourcesLimit,
        signal,
      });
      if (signal.aborted || term !== this.term || markup == null) return;
      this.panel.innerHTML = markup;
      // The fragment renders everything the API returned; the page cuts it to
      // its own numbers (the drawer does the same with its own).
      trimGroups(this.panel, {
        products: this.dataset.limitProducts,
        collections: this.dataset.limitCollections,
        articles: this.dataset.limitArticles,
      });
      // Nothing found is not a panel: the page below already answers that, and
      // an empty box under the field would just cover it.
      const empty = Boolean(this.panel.querySelector('[data-search-empty]'));
      this.panel.hidden = empty;
      this.input.setAttribute('aria-expanded', String(!empty));
    } catch {
      // A failed suggestion is silence, never an error over the results.
      this.close();
    }
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('ia-search-suggest')) {
  customElements.define('ia-search-suggest', IaSearchSuggest);
}
